import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import ObliqueIcon from '@/components/icons/ObliqueIcon';
import { useTenant } from '@/contexts/TenantContext';
import { storage } from '@/lib/firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';

export default function CreateTenantModal({ isOpen, onClose }) {
    const { createTenant } = useTenant();
    
    const [formData, setFormData] = useState({
        name: '',
        domain: '',
        description: '',
        industry: ''
    });

    const [logoFiles, setLogoFiles] = useState({ light: null, dark: null });
    
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const handleChange = (e) => {
        setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
    };

    const handleFileChange = (e, type) => {
        const file = e.target.files[0];
        if (file) {
            setLogoFiles(prev => ({ ...prev, [type]: file }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);
        setLoading(true);
        
        try {
            let lightLogoUrl = '';
            let darkLogoUrl = '';

            const tenantIdBase = `tenant-${formData.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;

            if (logoFiles.light) {
                const storageRef = ref(storage, `tenants/${tenantIdBase}/light-logo-${Date.now()}`);
                await uploadBytes(storageRef, logoFiles.light);
                lightLogoUrl = await getDownloadURL(storageRef);
            }

            if (logoFiles.dark) {
                const storageRef = ref(storage, `tenants/${tenantIdBase}/dark-logo-${Date.now()}`);
                await uploadBytes(storageRef, logoFiles.dark);
                darkLogoUrl = await getDownloadURL(storageRef);
            }

            await createTenant({
                ...formData,
                lightLogoUrl,
                darkLogoUrl
            });

            setFormData({ name: '', domain: '', description: '', industry: '' });
            setLogoFiles({ light: null, dark: null });
            onClose();
        } catch (err) {
            setError(err.message || 'Failed to create new Account');
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    const InputField = ({ label, name, placeholder, type = "text", required = false }) => (
        <div>
            <label className="block text-[11px] font-semibold text-[var(--text-secondary)] mb-2 uppercase tracking-[0.05em]">
                {label} {required && <span className="text-red-500">*</span>}
            </label>
            {type === 'textarea' ? (
                <textarea
                    name={name}
                    value={formData[name]}
                    onChange={handleChange}
                    placeholder={placeholder}
                    required={required}
                    rows={3}
                    className="w-full px-4 py-3 rounded-xl border border-[var(--border)] bg-[var(--bg-muted)] text-[var(--text-primary)] text-[13px] outline-none transition-all duration-200 resize-y focus:border-[var(--accent)]"
                />
            ) : (
                <input
                    type={type}
                    name={name}
                    value={formData[name]}
                    onChange={handleChange}
                    placeholder={placeholder}
                    required={required}
                    className="w-full px-4 py-3 rounded-xl border border-[var(--border)] bg-[var(--bg-muted)] text-[var(--text-primary)] text-[13px] outline-none transition-all duration-200 focus:border-[var(--accent)]"
                />
            )}
        </div>
    );

    const FileUploadField = ({ label, type }) => (
        <div>
            <label className="block text-[11px] font-semibold text-[var(--text-secondary)] mb-2 uppercase tracking-[0.05em]">
                {label}
            </label>
            <div className="relative w-full px-4 py-3 rounded-xl border border-dashed border-[var(--border)] bg-[var(--bg-muted)] flex items-center gap-3 cursor-pointer transition-all duration-200 hover:border-[var(--accent)] hover:bg-[var(--bg-surface)]">
                <input 
                    type="file" 
                    accept="image/svg+xml,image/png,image/jpeg"
                    onChange={(e) => handleFileChange(e, type)}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                />
                <div className="w-8 h-8 rounded-lg bg-[var(--bg-surface)] border border-[var(--border)] flex items-center justify-center shrink-0">
                    <ObliqueIcon name="cloud_upload" size={16} color="var(--text-tertiary)" />
                </div>
                <div className="flex-1 overflow-hidden">
                    <div className={`text-[13px] font-medium whitespace-nowrap overflow-hidden text-ellipsis ${logoFiles[type] ? 'text-[var(--text-primary)]' : 'text-[var(--text-tertiary)]'}`}>
                        {logoFiles[type] ? logoFiles[type].name : 'Click to upload SVG or PNG'}
                    </div>
                </div>
            </div>
        </div>
    );

    return (
        <AnimatePresence>
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/40 backdrop-blur-md z-[9999] flex items-center justify-center p-6"
            >
                <motion.div
                    initial={{ opacity: 0, scale: 0.95, y: 20 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95, y: 20 }}
                    className="bg-[var(--bg-surface)] rounded-[24px] w-full max-w-[800px] border border-[var(--border)] shadow-[0_24px_48px_rgba(0,0,0,0.2)] overflow-hidden flex flex-col max-h-[90vh]"
                >
                    <div className="px-8 py-6 border-b border-[var(--border)] flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <div className="w-12 h-12 rounded-2xl bg-indigo-500/10 text-indigo-500 flex items-center justify-center">
                                <ObliqueIcon name="domain_add" size={24} />
                            </div>
                            <div>
                                <h2 className="text-[20px] font-semibold m-0 text-[var(--text-primary)]">Register Business Entity</h2>
                                <p className="text-[13px] text-[var(--text-secondary)] mt-1 mb-0">Initialize a fully isolated SaaS Tenant.</p>
                            </div>
                        </div>
                        <button onClick={onClose} className="bg-[var(--bg-muted)] border border-[var(--border)] rounded-xl cursor-pointer text-[var(--text-tertiary)] p-2.5 flex items-center justify-center transition-all duration-200 hover:bg-[var(--bg-surface)] hover:text-[var(--text-primary)]">
                            <ObliqueIcon name="close" size={20} />
                        </button>
                    </div>

                    <div className="p-8 overflow-y-auto custom-scrollbar">
                        {error && (
                            <div className="bg-red-500/10 text-red-500 p-4 rounded-xl text-[13px] mb-6 flex items-center gap-2">
                                <ObliqueIcon name="error_outline" size={18} />
                                {error}
                            </div>
                        )}

                        <form id="tenant-form" onSubmit={handleSubmit} className="flex flex-col gap-6">
                            <div className="grid grid-cols-2 gap-5">
                                <InputField label="Legal Entity Name" name="name" placeholder="e.g. Your Company Name" required />
                                <InputField label="Primary Domain" name="domain" placeholder="e.g. yourcompany.com" required />
                            </div>
                            
                            <InputField label="Industry / Vertical" name="industry" placeholder="e.g. Digital Marketing, SaaS, Events" />
                            
                            <InputField label="Entity Description" name="description" type="textarea" placeholder="Brief description of the organization and its primary function..." />
                            
                            <div className="grid grid-cols-2 gap-5">
                                <FileUploadField label="Light Mode Logo Upload" type="light" />
                                <FileUploadField label="Dark Mode Logo Upload" type="dark" />
                            </div>
                        </form>
                    </div>

                    <div className="px-8 py-6 border-t border-[var(--border)] bg-[var(--bg-muted)] flex justify-end gap-3">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-6 py-3 rounded-xl bg-[var(--bg-surface)] border border-[var(--border)] text-[var(--text-primary)] text-[13px] font-semibold cursor-pointer transition-colors duration-200 hover:bg-[var(--bg-page)]"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            form="tenant-form"
                            disabled={loading}
                            className={`px-8 py-3 rounded-xl bg-[var(--accent)] border-none text-white text-[13px] font-semibold cursor-pointer flex items-center justify-center gap-2 shadow-[0_4px_12px_rgba(99,102,241,0.3)] transition-all duration-200 hover:opacity-90 ${loading ? 'opacity-70 cursor-not-allowed' : ''}`}
                        >
                            {loading ? 'Provisioning...' : 'Provision Entity'}
                        </button>
                    </div>
                </motion.div>
            </motion.div>
        </AnimatePresence>
    );
}
